import type { RichTextEditorTag } from "@v1/types/rich-text-editor";
import type { SavedTextStyle } from "schemas/generated/designLibrary";

import * as React from "react";

import { Separator } from "@replo/design-system/components/separator/Separator";
import { DebouncedInput } from "@v1/components/common/designSystem/Input";
import Popover from "@v1/components/designSystem/popover/Popover";
import ControlGroup from "@v1/components/editor/page/element-editor/components/extras/ControlGroup";
import FontFamilySelector from "@v1/components/editor/page/element-editor/components/selectors/text-style/FontFamilySelector";
import FontSizeSelector from "@v1/components/editor/page/element-editor/components/selectors/text-style/FontSizeSelector";
import FontWeightSelector from "@v1/components/editor/page/element-editor/components/selectors/text-style/FontWeightSelector";
import LetterSpacingSelector from "@v1/components/editor/page/element-editor/components/selectors/text-style/LetterSpacingSelector";
import LineHeightSelector from "@v1/components/editor/page/element-editor/components/selectors/text-style/LineHeightSelector";
import TagSelector from "@v1/components/editor/page/element-editor/components/selectors/text-style/TagSelector";
import useGetStoreNameAndUrl from "@v1/hooks/useGetStoreNameAndUrl";

import ShopStylePreviewText from "./ShopStylePreviewText";

type TextSavedStyleModifierProps = {
  isPopoverOpen: boolean;
  onPopoverOpenChange: (isOpen: boolean) => void;
  textSavedStyle: SavedTextStyle & { id: string };
  handleUpdateTextSavedStyle: (
    updatedStyle: SavedTextStyle & { id: string },
  ) => void;
  sideOffset?: number;
};

const htmlTagToRichTextEditorTag = (htmlTag: string): RichTextEditorTag => {
  if (htmlTag === "p") {
    return "P";
  }
  const num = htmlTag.slice(1);
  return num as RichTextEditorTag;
};

const richTextEditorTagToHtmlTag = (
  tag: RichTextEditorTag,
): "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" => {
  if (tag === "P") {
    return "p";
  }
  return `h${tag}` as "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";
};

const TextSavedStyleModifier: React.FC<TextSavedStyleModifierProps> = ({
  isPopoverOpen,
  onPopoverOpenChange,
  textSavedStyle,
  sideOffset,
  handleUpdateTextSavedStyle,
}) => {
  const { storeName } = useGetStoreNameAndUrl();

  return (
    <Popover.Root isOpen={isPopoverOpen} onOpenChange={onPopoverOpenChange}>
      <Popover.Content
        shouldPreventDefaultOnInteractOutside
        side="right"
        align="start"
        className="w-[250px] p-2"
        title="Edit Text Style"
        sideOffset={sideOffset}
        onInteractOutside={() => {
          onPopoverOpenChange(false);
        }}
      >
        <div className="flex flex-col gap-2">
          <div className="bg-neutral-soft flex h-16 w-full items-center justify-center rounded-lg p-2">
            <ShopStylePreviewText
              attributes={textSavedStyle.attributes}
              text={storeName || "Your Brand"}
              className="overflow-x-hidden truncate text-[24px] leading-[32px]"
            />
          </div>

          <ControlGroup label="Name">
            <DebouncedInput
              id="name"
              placeholder="Name"
              value={textSavedStyle.name}
              onValueChange={(value) => {
                handleUpdateTextSavedStyle({
                  ...textSavedStyle,
                  name: value,
                });
              }}
            />
          </ControlGroup>

          <Separator />

          <TagSelector
            currentTag={htmlTagToRichTextEditorTag(
              textSavedStyle.attributes.htmlTag,
            )}
            onChangeTag={(richTextTag) => {
              const htmlTag = richTextEditorTagToHtmlTag(richTextTag);
              handleUpdateTextSavedStyle({
                ...textSavedStyle,
                attributes: {
                  ...textSavedStyle.attributes,
                  htmlTag,
                },
              });
            }}
          />

          <FontFamilySelector
            fontFamily={textSavedStyle.attributes.fontFamily}
            onSelect={(fontFamily) => {
              handleUpdateTextSavedStyle({
                ...textSavedStyle,
                attributes: {
                  ...textSavedStyle.attributes,
                  fontFamily: fontFamily ?? null,
                },
              });
            }}
            placeholder="Select a font"
          />

          <FontWeightSelector
            fontFamily={textSavedStyle.attributes.fontFamily}
            fontWeight={textSavedStyle.attributes.fontWeight}
            onSelect={(fontWeight) => {
              handleUpdateTextSavedStyle({
                ...textSavedStyle,
                attributes: {
                  ...textSavedStyle.attributes,
                  fontWeight: fontWeight as
                    | "100"
                    | "200"
                    | "300"
                    | "400"
                    | "500"
                    | "600"
                    | "700"
                    | "800"
                    | "900"
                    | "950",
                },
              });
            }}
          />

          <FontSizeSelector
            size={textSavedStyle.attributes.fontSize ?? null}
            onChange={(fontSize) => {
              handleUpdateTextSavedStyle({
                ...textSavedStyle,
                attributes: {
                  ...textSavedStyle.attributes,
                  fontSize,
                },
              });
            }}
          />

          <LineHeightSelector
            lineHeight={textSavedStyle.attributes.lineHeight ?? null}
            onChange={(lineHeight) => {
              handleUpdateTextSavedStyle({
                ...textSavedStyle,
                attributes: { ...textSavedStyle.attributes, lineHeight },
              });
            }}
          />

          <LetterSpacingSelector
            onChange={(letterSpacing) => {
              handleUpdateTextSavedStyle({
                ...textSavedStyle,
                attributes: { ...textSavedStyle.attributes, letterSpacing },
              });
            }}
          />
        </div>
      </Popover.Content>
      <Popover.Anchor className="left-0 top-0 sticky" />
    </Popover.Root>
  );
};

export default TextSavedStyleModifier;
